import { Avatar, AvatarFallback, AvatarImage } from "@/components/atoms/Avatar";
import { Text } from "@/components/atoms/Text";
import { cn } from "@/lib/utils";
import Flag16 from "@/icons/__generated__/FlagFilled/FlagFilled16";
import { ReactNode } from "react";
import { format } from "date-fns";

export interface ChatMessageProps {
  children: ReactNode;
  timestamp: Date | string;
  isSent?: boolean;
  avatarUrl?: string;
  avatarAlt?: string;
  className?: string;
  flagText?: string;
  onFlagMessageClicked?: () => void;
}

export const ChatMessage = ({
  children,
  timestamp,
  isSent = true,
  avatarUrl,
  avatarAlt,
  className,
  flagText,
  onFlagMessageClicked,
}: ChatMessageProps) => {
  const formattedTime = typeof timestamp === 'string' ? timestamp : format(timestamp, 'h:mm a');

  return (
    <div className={cn("tw-flex tw-gap-8 tw-group", isSent ? "tw-flex-row-reverse" : "tw-flex-row", className)}>
      {!isSent && (
        <div className="tw-flex-shrink-0 tw-self-end tw-mb-[30px]">
          <Avatar size={32}>
            {avatarUrl ? (
              <AvatarImage src={avatarUrl} alt={avatarAlt || "User avatar"} />
            ) : (
              <AvatarFallback />
            )}
          </Avatar>
        </div>
      )}
      <div className={cn(
        "tw-flex tw-flex-col tw-w-full",
        isSent ? "tw-items-end tw-gap-2" : "tw-items-start",
        "tw-flex-1"
      )}>
        {children}
        <div className="tw-flex tw-items-center tw-gap-8">
          <Text
            typeface="caption-2-m"
            className={cn(
              "tw-text-gray-600",
              isSent ? "tw-text-right" : "tw-text-left",
              isSent ? "tw-ml-auto" : ""
            )}
          >
            {formattedTime}
          </Text>
          {!isSent && (
            <button
              onClick={onFlagMessageClicked}
              className="tw-mb-[5px] tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity"
              aria-label={flagText || "Report message"}
            >
              <Flag16 width={16} className="tw-text-gray-600" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

interface MessageBubbleProps {
  children: ReactNode;
  isSent?: boolean;
}

export const MessageBubble = ({ children, isSent = true }: MessageBubbleProps) => {
  return (
    <div
      className={cn(
        "tw-px-12 tw-py-8 tw-text-gray-900 tw-max-w-[75%] tw-whitespace-normal",
        isSent
          ? "tw-bg-purple-600 tw-bg-opacity-10 tw-rounded-t-[16px] tw-rounded-bl-[16px] tw-self-end"
          : "tw-bg-gray-200 tw-rounded-t-[16px] tw-rounded-br-[16px]"
      )}
    >
      {children}
    </div>
  );
};
